/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';

import {OBJECT_TYPE} from '../constants/object-type';
import {Downloader} from '../../../components/util/downloader';
import {sortStatValue, statComparator} from './comparators';
import {CHUNK_SIZE} from '../../../components/api/resources/resources-constants';
import {SearchResource} from '../../../components/api/resources/resource/search';
import {StatsRequestState, StatsResource} from '../../../components/api/resources/resource/stats-resources/stats-resource';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {formatCsvOutput} from '../filters/csv-output';
import {objectTypeStateParams} from '../filters/object-type-state-params';
import {OgTranslateService} from '../filters/og-translate.service';
import {formatLocaleDate} from '../locale/locale-date-format';
import {LocaleHelper} from '../../../components/util/locale-helper';
import {AdnTimeShifterService} from '../../../components/util/adn-time-shifter.service';
import {ChartOptionsUtil} from '../options/chart-options-util.service';

@Injectable({providedIn: 'root'})
export class QueryHelperService {
  constructor(
    private readonly ogTranslate: OgTranslateService,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    private readonly searchResource: SearchResource,
    private readonly translate: TranslateService,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly statsResource: StatsResource,
    private readonly localUserProfile: LocalUserProfile,
    private readonly localNetworkProfile: LocalNetworkProfile) {
  }

  private formatNetworkDate(value: any, format: string): string | null {
    const locale = LocaleHelper.toAngularFormat(this.localUserProfile.get('locale') || 'en_GB');
    return formatLocaleDate(value, format, locale, this.localNetworkProfile.get('timeZone') || 'utc');
  }

  private formatNetworkFilenameDate(value: any): string {
    const timeZone = this.localNetworkProfile.get('timeZone') || 'utc';
    return moment.tz(value, timeZone).format('M-D-YY-H-mm');
  }

  setUpQuery(ctrl, childObjectType, parentObjectType, superParentObjectType, options): () => void {
    ctrl.selectAllChildren = false;
    let xlsTitle = 'data.xls';
    const ASYNC_STATS_POLL_INTERVAL = 1000;
    let asyncStatsPollTimeout = null;
    const requestState: StatsRequestState = {};
    ctrl.useAsyncStats = _.get(options, 'useAsyncStats', false);
    ctrl.awaitingAsyncResultPayload = false;

    const cancelAsyncStatsPoll = () => {
      if (asyncStatsPollTimeout) {
        clearTimeout(asyncStatsPollTimeout);
        asyncStatsPollTimeout = null;
      }
    };

    ctrl.sel = {};
    ctrl.primaryType = childObjectType.type;
    ctrl.profileParam = childObjectType.queryColumnOptions;

    ctrl.availableSources = {
      DIRECT: true,
      SELF_SERVICE: this.localNetworkProfile.isSuiPlatform(),
      MARKETPLACE: this.localNetworkProfile.isMarketplacePlatform()
    };
    ctrl.source = '';
    ctrl.showSources = [OBJECT_TYPE.Creative, OBJECT_TYPE.LineItem, OBJECT_TYPE.Order].indexOf(childObjectType) > -1;
    ctrl.showLabels = childObjectType.type !== 'Creative';

    const preferredCurrency = this.localUserProfile.getPreferredCurrency();
    ctrl.availableCurrencies = this.localNetworkProfile.get('availableCurrencies');
    ctrl.currency = ctrl.availableCurrencies.indexOf(preferredCurrency) > -1 ? preferredCurrency : this.localNetworkProfile.get('defaultCurrency');

    const groupingTypes = [];
    const subGroupingTypes = ['child', 'parent', 'superParent', 'team'];
    if (this.localNetworkProfile.isMarketplacePlatform()) {
      subGroupingTypes.push('axProduct');
    }

    const addTimeChunkGrouping = (chunk) => {
      ctrl.text = ctrl.text || {};
      ctrl.text[chunk.key] = chunk.name.toUpperCase();
      ctrl.text[chunk.key + 'Plural'] = chunk.name;
      ctrl.text[chunk.key + 'LowerCase'] = chunk.name.toLowerCase();
      ctrl.text[chunk.key + 'PluralLowerCase'] = chunk.name.toLowerCase();
      ctrl[chunk.key + 'Type'] = {
        name: chunk.name,
        apiParam: 'chunk',
        caps: chunk.key.toUpperCase(),
        type: chunk.unit,
        timeChunk: true
      };
      groupingTypes.push(chunk.key);
    };

    const addObjectTypeGrouping = (theObjectType, prefix) => {
      if (!theObjectType) {
        return;
      }
      ctrl.text = ctrl.text || {};
      ctrl.text[prefix] = this.ogTranslate.transform(theObjectType.apiParam);
      ctrl.text[prefix + 'Plural'] = this.ogTranslate.transform(theObjectType.apiParam + '.plural');
      ctrl.text[prefix + 'LowerCase'] = ctrl.text[prefix].toLowerCase();
      ctrl.text[prefix + 'PluralLowerCase'] = ctrl.text[prefix + 'Plural'].toLowerCase();
      ctrl[prefix + 'Type'] = Object.assign({}, theObjectType);
      ctrl[prefix + 'Type'].name = ctrl.text[prefix];
      ctrl[prefix + 'Type'].type = ctrl.text[prefix];
      ctrl[prefix + 'Search'] = (searchElement) => {
        this.searchResource.query({q: searchElement.search, types: theObjectType.type}).then((data: any) => {
          ctrl[prefix + 'Results'] = data.searchResults;
        });
      };
      ctrl[prefix + 'ItemLink'] = (item) => {
        if (!theObjectType.stateName || !item) {
          return null;
        }

        const params = objectTypeStateParams(item, theObjectType);
        if (!params || _.every(params, function(value) {
          return value === undefined || value === null || value === '';
        })) {
          return null;
        }

        return {
          stateName: theObjectType.stateName,
          params: params
        };
      };
      groupingTypes.push(prefix);
    };

    const addTargetingGrouping = () => {
      ctrl.text = ctrl.text || {};
      ctrl.text.targeting = 'Targeting';
      ctrl.text.targetingPlural = 'Targeting';
      ctrl.text.targetingLowerCase = 'targeting';
      ctrl.text.targetingPluralLowerCase = 'targeting';
      ctrl.targetingType = 'TARGETING';
      groupingTypes.push('targeting');
    };

    addTimeChunkGrouping(CHUNK_SIZE.MONTHLY);
    addTimeChunkGrouping(CHUNK_SIZE.WEEKLY);
    addTimeChunkGrouping(CHUNK_SIZE.DAILY);
    addTimeChunkGrouping(CHUNK_SIZE.HOURLY);
    addTimeChunkGrouping(CHUNK_SIZE.HALF_HOURLY);
    addObjectTypeGrouping(childObjectType, 'child');
    addObjectTypeGrouping(parentObjectType, 'parent');
    addObjectTypeGrouping(superParentObjectType, 'superParent');
    addObjectTypeGrouping(OBJECT_TYPE.Team, 'team');
    if (this.localNetworkProfile.isMarketplacePlatform()) {
      addObjectTypeGrouping(OBJECT_TYPE.MarketplaceProduct, 'axProduct');
    }

    const allTypes = [childObjectType, parentObjectType, superParentObjectType];

    if (allTypes.indexOf(OBJECT_TYPE.Creative) > -1) {
      addObjectTypeGrouping(OBJECT_TYPE.Advertiser, 'advertiser');
      addObjectTypeGrouping(OBJECT_TYPE.AdUnit, 'adunit');
      addObjectTypeGrouping(OBJECT_TYPE.Site, 'site');
      addObjectTypeGrouping(OBJECT_TYPE.EarningsAccount, 'publisher');
    } else if (allTypes.indexOf(OBJECT_TYPE.Order) > -1 || allTypes.indexOf(OBJECT_TYPE.Advertiser) > -1) {
      addObjectTypeGrouping(OBJECT_TYPE.AdUnit, 'adunit');
      addObjectTypeGrouping(OBJECT_TYPE.Site, 'site');
      addObjectTypeGrouping(OBJECT_TYPE.EarningsAccount, 'publisher');
    } else if (allTypes.indexOf(OBJECT_TYPE.EarningsAccount) > -1) {
      addObjectTypeGrouping(OBJECT_TYPE.Creative, 'creative');
      addObjectTypeGrouping(OBJECT_TYPE.LineItem, 'lineitem');
      addObjectTypeGrouping(OBJECT_TYPE.Order, 'order');
      addObjectTypeGrouping(OBJECT_TYPE.Advertiser, 'advertiser');
      addTargetingGrouping();
    }

    if (allTypes.indexOf(OBJECT_TYPE.Folder) > -1) {
      ctrl.hideGrouping = true;
    } else if (allTypes.indexOf(OBJECT_TYPE.UserSegment) > -1) {
      ctrl.grouping = OBJECT_TYPE.LineItem;
      ctrl.hideGrouping = true;
    } else {
      addObjectTypeGrouping(OBJECT_TYPE.Tier, 'tier');
    }

    ctrl.groupingSelections = _.map(_.filter(groupingTypes, function(check) {
      return ctrl[check + 'Type'];
    }), function(c) {
      return {val: ctrl[c + 'Type'], text: ctrl.text[c + 'Plural']};
    });
    ctrl.grouping = ctrl.childType;

    const noneGroup = {val: '', text: 'None'};
    ctrl.subGroupingSelections = [noneGroup].concat(_.map(_.filter(subGroupingTypes, function(check) {
      return ctrl[check + 'Type'];
    }), function(c) {
      return {val: ctrl[c + 'Type'], text: ctrl.text[c + 'Plural']};
    }));
    ctrl.grouping2 = noneGroup.val;

    ctrl.transParams = '{objectType: "' + ctrl.text.childPluralLowerCase + '"}';
    ctrl.adnKey = ['LINE_ITEM', 'ADVERTISER', 'ORDER'].indexOf(childObjectType.caps) > -1 ? 'advObjectLabel' : 'pubObjectLabel';
    ctrl.advObjectLabel = [];
    ctrl.pubObjectLabel = [];
    ctrl.includeAll = false;
    ctrl.showPoliticalStatsOption = this.localNetworkProfile.isPoliticalAdsPlatform();
    ctrl.political = null;
    ctrl.dateOption = this.ChartOptionsUtil.getDateOptions().last7Days;
    ctrl.changeCallback = (dateOptionId, startDate, endDate) => {
      ctrl.dateOption = this.ChartOptionsUtil.getDateOptions()[dateOptionId];
      ctrl.startDate = startDate;
      ctrl.endDate = endDate;
    };

    const chartOptions = _.cloneDeep(this.ChartOptionsUtil.getOptions(childObjectType.caps));
    ctrl.fields = _.map(_.filter(chartOptions.queryFields, (f) => {
      return !f.availability || !this.localNetworkProfile.isPureMarketplacePlatform() || f.availability.MARKETPLACE;
    }), function(v) {
      return v;
    });
    ctrl.fields = _.filter(ctrl.fields, function(f) {
      return f.yAxis !== -1;
    });
    ctrl.fields.unshift({id: 'labels', name: 'Labels', csvName: 'Labels', apiId: 'labels'});
    ctrl.fields.unshift({id: 'name', name: 'Name', csvName: ctrl.text.child, apiId: childObjectType.apiParam});

    ctrl.changeHook = {
      fieldChange: function() {
        const hasCustom = _.find(ctrl.sel.fullFields, function(f) {
          return f.id === 'custom';
        });
        const customFields = hasCustom ? _.filter(ctrl.tableFields, function(f) {
          return f.extra;
        }) : [];
        ctrl.tableFields = _.filter(_.cloneDeep(ctrl.sel.fullFields).concat(customFields), function(f) {
          return f.id !== 'custom';
        });
      }
    };

    const regNoObjects = (reason) => {
      ctrl.queryHappening = false;
      ctrl.reasons = reason;
      ctrl.loaded = true;
      ctrl.asyncQueryStatus = null;
      ctrl.totals = undefined;
    };

    ctrl.sorting = {};
    ctrl.sorting.makeSortQuery = function(param) {
      // Match display keys (apiId/altId) so e.g. eCpm sorts grossECpm, not a missing eCpm.
      const sortKey = param.apiId || param.altId || param.id;
      const sortExtra = param.extra;
      if (ctrl.sorting.orderByParam === sortKey && ctrl.sorting.orderByExtra === sortExtra) {
        ctrl.sorting.asc = !ctrl.sorting.asc;
      } else {
        ctrl.sorting.orderByParam = sortKey;
        ctrl.sorting.orderByExtra = sortExtra;
        ctrl.sorting.asc = false;
      }
    };
    ctrl.sorting.groupSortValue = function(item) {
      return sortStatValue(_.get(item, ['totals', ctrl.sorting.orderByParam]), ctrl.sorting.orderByExtra);
    };
    ctrl.sorting.rowSortValue = function(item) {
      return sortStatValue(_.get(item, [ctrl.sorting.orderByParam]), ctrl.sorting.orderByExtra);
    };
    ctrl.sorting.sortQuery = {$resolved: true};
    ctrl.sorting.theParams = _.map(ctrl.fields, function(f) {
      return {
        param: f.id !== 'name' && f.id !== 'labels' ? f.id : undefined,
        label: f.id,
        numericColumn: f.id !== 'name' && f.id !== 'labels'
      };
    });
    ctrl.sorting.orderByParam = 'impressions';
    ctrl.sorting.asc = false;
    ctrl.sortComparator = statComparator;

    const assignDownloadFields = () => {
      if (ctrl.sel.allFields) {
        ctrl.downloadFields = _.map(ctrl.csvFields, function(f) {
          return f.field;
        });
        ctrl.downloadColumnOrder = _.map(ctrl.csvFields, function(f) {
          return f.columnOrder;
        });
        return;
      }
      const selFullFields = _.map(ctrl.tableFields, function(f) {
        return f.csvName || f.name;
      });
      let selFieldsOnly = _.filter(ctrl.csvFields, function(f) {
        return f.grouping;
      });
      selFieldsOnly = selFieldsOnly.concat(_.filter(ctrl.fields, function(f) {
        return (ctrl.grouping === ctrl.childType && f.id === 'name') || (ctrl.grouping === ctrl.childType && f.id === 'labels') || selFullFields.indexOf(f.csvName || f.name) > -1 || f.extra;
      }));
      selFieldsOnly = selFieldsOnly.concat(_.filter(ctrl.csvFields, function(f) {
        return f.extra;
      }));
      ctrl.downloadFields = _.map(selFieldsOnly, function(f) {
        return f.field || f.csvTranslateField || f.csvName || f.name;
      });
      ctrl.downloadColumnOrder = _.map(selFieldsOnly, function(f) {
        return f.columnOrder || f.apiId || f.altId || f.id;
      });
    };

    ctrl.saveDownloadFields = function() {
      assignDownloadFields();
    };
    ctrl.saveXls = () => {
      ctrl.saveDownloadFields();
      new Downloader().saveXls2(xlsTitle, ctrl.downloadFields, ctrl.downloadColumnOrder, ctrl.getRows(), ctrl.fields, true);
    };

    ctrl.getRows = () => {
      let data = _.cloneDeep(ctrl.rows);

      if (data[0].chunks) {
        _.forEach(data, (dContainer) => {
          const parentEntity = dContainer[ctrl.grouping.apiParam] || {};
          const parentName = parentEntity.name;
          const parentLabels = parentEntity.labels;
          const parentSource = parentEntity.source;
          _.forEach(dContainer.chunks, (d) => {
            d.startDate = d.startDate || _.get(d, ['lineItem', 'startDate']);
            d.endDate = d.endDate || _.get(d, ['lineItem', 'endDate']);
            _.forEach(ctrl.csvFields, (f) => {
              const isLabel = f.columnOrder.toLowerCase().indexOf('label') > -1;
              if (f.columnOrder === ctrl.withGrouping2.apiParam) {
                const grouping2Entity = d[ctrl.withGrouping2.apiParam] || {};
                const name = grouping2Entity.name;
                const labels = _.map(grouping2Entity.labels, function(l) {
                  return l.text || l;
                }) || [];
                const source = grouping2Entity.source;
                if (source) {
                  labels.push(this.translate.instant('lineItem.source.' + source));
                }
                d[ctrl.withGrouping2.apiParam] = name;
                d[f.columnOrder] = name;
                d.labels = _.isArray(labels) ? labels.join(', ') : '';
              } else if (f.columnOrder === ctrl.grouping.apiParam) {
                const groupingLabels = _.map(parentLabels, function(l) {
                  return l.text || l;
                }) || [];
                if (parentSource) {
                  groupingLabels.push(this.translate.instant('lineItem.source.' + parentSource));
                }
                d[ctrl.grouping.apiParam] = parentName;
                d[ctrl.grouping.apiParam + 'labels'] = _.isArray(groupingLabels) ? groupingLabels.join(', ') : '';
              } else {
                d[f.columnOrder] = isLabel ? d[f.apiId || f.altId || f.columnOrder] || '' : formatCsvOutput(d[f.apiId || f.altId || f.columnOrder], f.extra || f.columnOrder) || 0;
              }
            });
          });
        });
        data = _.flatten(_.map(data, function(d) {
          return d.chunks;
        }));
      } else {
        _.forEach(data, (d) => {
          d.startDate = d.startDate || _.get(d, ['lineItem', 'startDate']);
          d.endDate = d.endDate || _.get(d, ['lineItem', 'endDate']);
          _.forEach(ctrl.csvFields, (f) => {
            const isLabel = f.columnOrder.toLowerCase().indexOf('label') > -1;
            if (f.columnOrder === ctrl.withGrouping2.apiParam || f.columnOrder === ctrl.withGrouping.apiParam) {
              const groupingParam = f.columnOrder === ctrl.withGrouping2.apiParam ? ctrl.withGrouping2.apiParam : ctrl.withGrouping.apiParam;
              const groupingEntity = d[groupingParam] || {};
              const name = groupingEntity.name;
              const labels = _.map(groupingEntity.labels, function(l) {
                return l.text || l;
              }) || [];
              const source = groupingEntity.source;
              if (source) {
                labels.push(this.translate.instant('lineItem.source.' + source));
              }
              d[groupingParam] = name;
              const labelsParam = f.columnOrder === ctrl.withGrouping2.apiParam ? ctrl.withGrouping2.apiParam + 'labels' : ctrl.withGrouping.apiParam + 'labels';
              const labelValue = _.isArray(labels) ? labels.join(', ') : '';
              d.labels = labelValue;
              d[labelsParam] = labelValue;
            } else {
              const value = d[f.apiId || f.altId || f.columnOrder];
              d[f.columnOrder] = isLabel ? _.get(value, 'labels', value) || '' : formatCsvOutput(_.get(value, 'name', value), f.extra || f.columnOrder) || 0;
            }
          });
        });
      }
      return data;
    };

    ctrl.getTotals = () => {
      cancelAsyncStatsPoll();
      ctrl.searchMade = true;
      ctrl.queryHappening = true;
      ctrl.awaitingAsyncResultPayload = false;
      ctrl.reasons = undefined;
      ctrl.error = false;
      ctrl.totals = null;
      ctrl.rows = null;
      ctrl.asyncQueryStatus = null;
      ctrl.tableTitle = undefined;

      ctrl.startDateCall = ctrl.dateOption ? ctrl.dateOption.startDate().toDate() : moment(ctrl.startDate);
      ctrl.endDateCall = ctrl.dateOption ? ctrl.dateOption.endDate().toDate() : moment(ctrl.endDate);

      ctrl.children = ctrl.children || [];
      ctrl.parent = ctrl.parent || [];
      ctrl.superParent = ctrl.superParent || [];

      if (!ctrl.children.length && !ctrl.parent.length && !ctrl.superParent.length && !ctrl.selectAllChildren && !ctrl.advObjectLabel.length && !ctrl.pubObjectLabel.length) {
        regNoObjects('No ' + ctrl.text.childPluralLowerCase + ' have been found to retrieve stats for.');
        return;
      }

      ctrl.tableTitle = 'Totals for Selected ' + ctrl.text.childPlural + ' ' + this.formatNetworkDate(ctrl.startDateCall, 'short') + ' &ndash; ' + this.formatNetworkDate(ctrl.endDateCall, 'short');
      const tableTitle = 'totals-' + ctrl.text.childPlural.toLowerCase() + '-' + this.formatNetworkFilenameDate(ctrl.startDateCall) + '-' + this.formatNetworkFilenameDate(ctrl.endDateCall);
      ctrl.csvTableTitle = tableTitle + '.csv';
      xlsTitle = tableTitle + '.xls';

      ctrl.loaded = false;
      const params: any = {
        groupBy: childObjectType.caps,
        startDate: ctrl.startDateCall,
        endDate: ctrl.endDateCall,
        advObjectLabel: ctrl.advObjectLabel.join(';'),
        pubObjectLabel: ctrl.pubObjectLabel.join(';'),
        includeAll: ctrl.includeAll
      };

      _.forEach(ctrl.fields, (f) => {
        f.csvTranslateField = f.key ? this.ogTranslate.transform(f.key) : undefined;
      });
      ctrl.csvFields = _.map(ctrl.fields, function(f) {
        return {
          field: f.csvTranslateField || f.csvName || f.name,
          columnOrder: f.apiId || f.altId || f.id
        };
      });
      if (ctrl.grouping.timeChunk) {
        ctrl.sorting.orderByParam = 'name';
        ctrl.sorting.asc = true;
      } else {
        ctrl.sorting.orderByParam = 'impressions';
        ctrl.sorting.asc = false;
      }
      if (ctrl.grouping !== ctrl.childType) {
        params.groupBy = ctrl.grouping.caps;

        if (ctrl.grouping2) {
          if (childObjectType.type === 'UserSegment') {
            params.aggregateTo = null;
          } else {
            params.aggregateTo = ctrl.grouping2.caps || childObjectType.caps;
          }
          ctrl.csvFields.shift();
          ctrl.csvFields.unshift({field: ctrl.grouping2.name + ' Labels', columnOrder: 'labels', grouping: true});
          ctrl.csvFields.unshift({field: ctrl.grouping2.name, columnOrder: ctrl.grouping2.apiParam, grouping: true});
        }
        ctrl.csvFields.unshift({field: ctrl.grouping.name + ' Labels', columnOrder: ctrl.grouping.apiParam + 'labels', grouping: true});
        ctrl.csvFields.unshift({field: ctrl.grouping.name, columnOrder: ctrl.grouping.apiParam, grouping: true});
      }

      if (ctrl.selectAllChildren && !ctrl.bidCurrency) {
        params.advObjectLabel = '';
        params.pubObjectLabel = '';
        params.bidCurrency = null;
        if (childObjectType.type === 'UserSegment') {
          params.all = 'SEGMENT_TARGETS';
        } else if (!params.bidCurrency) {
          params.all = childObjectType.apiParam;
        }
      } else {
        params.bidCurrency = ctrl.bidCurrency;

        if (ctrl.children.length > 0) {
          if (childObjectType.type === 'UserSegment') {
            params.segmentTargets = _.join(_.map(ctrl.children, 'id'), ';');
            params.aggregateTo = null;
          } else {
            params[childObjectType.apiParam + 'Id'] = _.join(_.map(ctrl.children, 'id'), ';');
          }
        }
        if (ctrl.parent.length > 0) {
          params[parentObjectType.apiParam + 'Id'] = _.join(_.map(ctrl.parent, 'id'), ';');
        }
        if (ctrl.superParent.length > 0) {
          params[superParentObjectType.apiParam + 'Id'] = _.join(_.map(ctrl.superParent, 'id'), ';');
        }
      }
      params.source = ctrl.source;
      if (_.isBoolean(ctrl.political)) {
        params.political = ctrl.political;
      }

      const processResult = (data) => {
        cancelAsyncStatsPoll();
        ctrl.queryHappening = false;
        ctrl.awaitingAsyncResultPayload = false;
        ctrl.asyncQueryStatus = null;
        _.forEach(_.get(data, ['chunks'], []), (c) => {
          if (_.get(c, 'chunkStart')) {
            const formattedChunkStart = this.formatNetworkDate(c.chunkStart, 'short');
            c[ctrl.grouping.apiParam] = {name: formattedChunkStart};
          }
        });
        if (ctrl.grouping === 'TARGETING') {
          ctrl.rows = [];
          for (const [key, value] of Object.entries<any>(data)) {
            value.targeting = {name: key};
            ctrl.rows.push(value);
          }
          ctrl.withGrouping = {apiParam: 'targeting'};
        } else {
          ctrl.rows = data.chunks;
          ctrl.totals = data.totals;
          ctrl.withGrouping = _.assign({}, ctrl.grouping);
          ctrl.withGrouping2 = _.assign({}, ctrl.grouping !== ctrl.childType ? ctrl.grouping2 : ctrl.childType);
        }

        ctrl.tableFields = _.cloneDeep(ctrl.sel.fullFields);
        ctrl.tableFields = _.filter(ctrl.tableFields, function(f) {
          return !f.extra && f.id !== 'custom';
        });
        ctrl.csvFields = _.filter(ctrl.csvFields, function(f) {
          return !f.extra;
        });

        const showCustom = _.find(ctrl.sel.fullFields, function(f) {
          return f.id === 'custom';
        });
        if (showCustom) {
          const extras = this.ChartOptionsUtil.getLineItemCustomFields(ctrl.totals);
          const averages = this.ChartOptionsUtil.getCustomFieldsAverage();
          _.forEach(extras, (extra) => {
            const idExtra = extra.replace(/ /g, '-');
            const hasAverages = averages.indexOf(extra) > -1;
            const basicCountExtra = hasAverages ? ' count' : '';
            ctrl.tableFields.push({id: idExtra, name: extra + basicCountExtra, key: idExtra, text: extra + basicCountExtra, csvName: extra, columnOrder: extra + 'count', apiId: extra, extra: 'count'});
            ctrl.csvFields.push({field: extra + basicCountExtra, key: idExtra, columnOrder: extra + 'count', apiId: extra, extra: 'count'});
            if (hasAverages) {
              ctrl.tableFields.push({id: idExtra, name: extra + ' average', key: idExtra, text: extra + ' avg.', csvName: extra, columnOrder: extra + 'averageValue', apiId: extra, extra: 'averageValue'});
              ctrl.csvFields.push({field: extra + ' average', key: idExtra, columnOrder: extra + 'averageValue', apiId: extra, extra: 'averageValue'});
            }
            if (idExtra !== extra) {
              _.forEach(ctrl.rows, function(r) {
                if (r.extra) {
                  r[idExtra] = _.cloneDeep(r.extra);
                }
              });
            }
          });
        }

        assignDownloadFields();
        ctrl.loaded = true;

        if (!ctrl.rows || ctrl.rows.length < 1) {
          regNoObjects('No stats have been found for the ' + ctrl.text.childPluralLowerCase + ' requested.');
        }
      };

      const processError = (err) => {
        cancelAsyncStatsPoll();
        ctrl.awaitingAsyncResultPayload = false;
        ctrl.asyncQueryStatus = null;
        ctrl.objError = _.get(err, ['data', 'code']) === 'error.object.not.found';
        ctrl.error = true;
        ctrl.queryHappening = false;
      };

      const pollForAsyncStatsResult = (asyncId) => {
        if (!asyncId) {
          processError({data: {code: 'error.object.not.found'}});
          return;
        }
        const pollParams = _.assign({}, params, {id: asyncId});
        ctrl.awaitingAsyncResultPayload = true;
        this.statsResource.getAsync(pollParams, (data) => {
          ctrl.awaitingAsyncResultPayload = false;
          if (_.has(data, 'status')) {
            ctrl.asyncQueryStatus = {
              status: _.get(data, 'status'),
              positionInQueue: _.get(data, 'positionInQueue', null),
              secondsInQueue: _.get(data, 'secondsInQueue', null)
            };
            asyncStatsPollTimeout = setTimeout(() => {
              asyncStatsPollTimeout = null;
              pollForAsyncStatsResult(asyncId);
            }, ASYNC_STATS_POLL_INTERVAL);
            return;
          }
          processResult(data);
        }, processError, requestState);
      };

      if (ctrl.currency) {
        params.currency = ctrl.currency;
      }
      if (ctrl.grouping === 'TARGETING') {
        params.aggregateTo = null;
        this.statsResource.getTargeting(params, processResult, processError, requestState);
      } else if (ctrl.useAsyncStats) {
        ctrl.awaitingAsyncResultPayload = true;
        this.statsResource.getAsync(params, (data) => {
          ctrl.awaitingAsyncResultPayload = false;
          if (_.has(data, 'status')) {
            ctrl.asyncQueryStatus = {
              status: _.get(data, 'status')
            };
            pollForAsyncStatsResult(_.get(data, 'id'));
            return;
          }
          processResult(data);
        }, processError, requestState);
      } else {
        this.statsResource.get(params, processResult, processError, requestState);
      }
    };

    return () => {
      cancelAsyncStatsPoll();
      requestState.canceller?.resolve('Query page destroyed');
      requestState.canceller = undefined;
      requestState.isPending = false;
    };
  }
}
